import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, ExternalLink, AlertCircle } from 'lucide-react';

interface PDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  localUrl: string;
  title: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ isOpen, onClose, localUrl, title }) => {
  const { t } = useTranslation();
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const pdfUrl = useMemo(() => {
    if (!localUrl) return '';

    const absoluteUrl = localUrl.startsWith('http')
      ? localUrl
      : `${window.location.origin}${localUrl.startsWith('/') ? '' : '/'}${localUrl}`;

    return `${absoluteUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;
  }, [localUrl]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !localUrl) return;

    setIsLoading(true);
    setPdfLoadError(false);

    const controller = new AbortController();

    fetch(localUrl, { method: 'HEAD', signal: controller.signal })
      .then(response => {
        const contentType = response.headers.get('content-type') || '';
        if (!response.ok || !contentType.includes('pdf')) {
          throw new Error('PDF file is not available');
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          setPdfLoadError(true);
          setIsLoading(false);
        }
      });

    const timer = window.setTimeout(() => {
      setIsLoading(false);
    }, 1200);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [isOpen, localUrl]);

  if (!isOpen) return null;

  const downloadPDF = () => {
    const link = document.createElement('a');
    link.href = localUrl;
    link.download = `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(pdfUrl || localUrl, '_blank', 'noopener,noreferrer');
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setPdfLoadError(true);
    setIsLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-viewer-title"
    >
      <div className="mx-auto flex h-[100dvh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl sm:h-[calc(100dvh-2rem)] sm:rounded-lg">
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3 py-3 sm:px-4">
          <h2 id="pdf-viewer-title" className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900 sm:text-xl">
            {title}
          </h2>
          <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
            <button
              onClick={openInNewTab}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
              title={t('newsletter.actions.openBrowser')}
              aria-label={t('newsletter.actions.openBrowser')}
            >
              <ExternalLink className="h-5 w-5" />
            </button>
            <button
              onClick={downloadPDF}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
              title={t('newsletter.actions.downloadPdf')}
              aria-label={t('newsletter.actions.downloadPdf')}
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
              title="Close"
              aria-label="Close PDF viewer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 bg-gray-100">
          {isLoading && !pdfLoadError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-600 sm:text-base">{t('newsletter.viewer.loading')}</p>
              </div>
            </div>
          )}

          {pdfLoadError ? (
            <div className="flex h-full flex-col items-center justify-center px-6 py-8 text-center">
              <AlertCircle className="mb-4 h-14 w-14 text-blue-500 sm:h-16 sm:w-16" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('newsletter.viewer.notAvailable')}
              </h3>
              <p className="mb-6 max-w-md text-sm text-gray-600 sm:text-base">
                {t('newsletter.viewer.errorMessage')}
              </p>
              <div className="flex w-full max-w-sm flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center">
                <button
                  onClick={openInNewTab}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  <ExternalLink className="h-5 w-5" />
                  <span>{t('newsletter.actions.openBrowser')}</span>
                </button>
                <button
                  onClick={downloadPDF}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  <Download className="h-5 w-5" />
                  <span>{t('newsletter.actions.downloadPdf')}</span>
                </button>
              </div>
            </div>
          ) : (
            <iframe
              src={pdfUrl}
              className="h-full w-full border-0"
              title={title}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>

        <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between sm:px-4">
          <span className="hidden sm:block">{t('newsletter.viewer.browserControls')}</span>
          <span className="sm:hidden">{t('newsletter.viewer.mobileInstructions')}</span>
          <div className="flex items-center gap-4">
            <button
              onClick={openInNewTab}
              className="font-medium text-blue-600 transition-colors hover:text-blue-800"
            >
              {t('newsletter.viewer.openFullScreen')}
            </button>
            <button
              onClick={downloadPDF}
              className="font-medium text-green-600 transition-colors hover:text-green-800"
            >
              {t('newsletter.actions.downloadPdf')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
